import dayjs, { type Dayjs } from 'dayjs';

import { type ClassWithInstructors, type Direction, DirectionOption, type Option } from '../types';

interface FilterByInstructorProps {
    classes: ClassWithInstructors[];
    selectedInstructor: Option | null;
}

export const filterByInstructor = ({ classes, selectedInstructor }: FilterByInstructorProps): ClassWithInstructors[] => classes.filter((classValue) => {
    if (selectedInstructor) {
        const found = classValue.instructors.find((instructor) => instructor.email === selectedInstructor.email);

        return found;
    }
    return true;
});

interface FilterBySearchProps {
    classes: ClassWithInstructors[];
    searchValue: string;
}

export const filterBySearch = ({ classes, searchValue }: FilterBySearchProps): ClassWithInstructors[] => classes.filter((classValue) => {
    if (searchValue.length >= 1) {
        const className = classValue?.class_name;
        const matchesClassName = className ? className.toLocaleLowerCase().includes(searchValue.trim().toLocaleLowerCase()) : false;

        const classUuid = classValue?.class_uuid ?? '';
        const meetingId = classValue?.meeting_id ?? '';

        const matchesClassUuid = (searchValue.length > 5) && classUuid.toLocaleLowerCase().includes(searchValue.trim().toLocaleLowerCase());
        const matchesMeetingUuid = (searchValue.length > 5) && meetingId.toLocaleLowerCase().includes(searchValue.trim().toLocaleLowerCase());

        return matchesClassName || matchesClassUuid || matchesMeetingUuid;
    }
    return true;
});

interface ReturnClassDateStringProps {
    index: 'first' | 'last';
    classDates?: string | null;
}

export const returnClassDateString = ({ classDates, index }: ReturnClassDateStringProps) => {
    if (typeof classDates !== 'string') return null;

    const classDatesArray = classDates.split(',');
    const indexNumber = index === 'first' ? 0 : classDatesArray.length - 1;
    const date = classDatesArray[indexNumber];

    if (!date) return null;

    const lastClassDate = date.split(':')[0];

    if (!lastClassDate) return null;

    return lastClassDate;
};

interface FilterByStartDateProps {
    classes: ClassWithInstructors[];
    startDate: Dayjs | null;
}

export const filterByStartDate = ({ classes, startDate }: FilterByStartDateProps): ClassWithInstructors[] => classes.filter((classValue) => {
    if (!startDate) return true;

    const classStartDateString = classValue.starting_at;

    if (!classStartDateString) {
        return false;
    }

    const classStartDate = dayjs(classStartDateString);

    const isBeforeSelectedDay = startDate.isBefore(classStartDate.toISOString(), 'day');
    const isSameSelectedDay = startDate.isSame(classStartDate.toISOString(), 'day');

    return isBeforeSelectedDay || isSameSelectedDay;
});

interface FilterByEndDateProps {
    classes: ClassWithInstructors[];
    endDate: Dayjs | null;
}

export const filterByEndDate = ({ classes, endDate }: FilterByEndDateProps): ClassWithInstructors[] => classes.filter((classValue) => {
    if (!endDate) return true;

    const classEndDateString = classValue.ending_at;

    if (!classEndDateString) {
        return false;
    }

    const classEndDate = dayjs(classEndDateString);

    const isAfterSelectedDay = endDate.isAfter(classEndDate.toISOString(), 'day');
    const isSameSelectedDay = endDate.isSame(classEndDate.toISOString(), 'day');

    return isAfterSelectedDay || isSameSelectedDay;
});

interface SortByProps {
    classes: ClassWithInstructors[];
    sort: Direction;
}

export const sortByClassName = ({ classes, sort }: SortByProps): ClassWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classes.sort((classA, classB) => classA.class_name.trim().localeCompare(classB.class_name.trim()));
        case DirectionOption.DESCENDING:
            return classes.sort((classA, classB) => classB.class_name.trim().localeCompare(classA.class_name.trim()));
        default:
            return classes;
    }
};

export const sortByDates = ({ classes, sort }: SortByProps): ClassWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classes.sort((a, b) => {
                if (!a.starting_at) return 1;
                if (!b.starting_at) return -1;
                return new Date(a.starting_at).getTime() - new Date(b.starting_at).getTime();
            });
        case DirectionOption.DESCENDING:
            return classes.sort((a, b) => {
                if (!a.starting_at) return -1;
                if (!b.starting_at) return 1;
                return new Date(b.starting_at).getTime() - new Date(a.starting_at).getTime();
            });
        default:
            return classes;
    }
};

const returnInstructorFirstName = (classWithInstructors: ClassWithInstructors): string | undefined | null => classWithInstructors?.instructors[0]?.user_name;

export const sortByInstructors = ({ classes, sort }: SortByProps): ClassWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classes.sort((classA, classB) => {
                const instructorNameA = returnInstructorFirstName(classA);
                const instructorNameB = returnInstructorFirstName(classB);

                if (instructorNameA && instructorNameB) {
                    return instructorNameA.trim().localeCompare(instructorNameB.trim());
                }

                return instructorNameA ? -1 : 1;
            });
        case DirectionOption.DESCENDING:
            return classes.sort((classA, classB) => {
                const instructorNameA = returnInstructorFirstName(classA);
                const instructorNameB = returnInstructorFirstName(classB);

                if (instructorNameA && instructorNameB) {
                    return instructorNameB.trim().localeCompare(instructorNameA.trim());
                }

                return instructorNameA ? -1 : 1;
            });
        default:
            return classes;
    }
};
